import { useContext } from 'react';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import AppTheme from "../shared-theme/AppTheme";
import { Link as RouterLink } from "react-router-dom";

import MuscleHeadTextIcon from "../../assets/MuscleHeadTextIcon";

import LanguageSelect from "../shared-theme/components/LanguageSelect";
import { useTranslation } from "react-i18next";
import { getOurSupportEmail } from "@config/config"
import { useLoginMutation } from "@mutation/authMutation";
import { AlertContext } from "@context/AlertProvider";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(350, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(350, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(350, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(350, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100vh",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  position: "relative",
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(340, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(340, 100%, 16%, 0.5), hsla(350, 30%, 5%))",
    }),
  },
}));

export default function SignIn(props) {
  const { t } = useTranslation(["auth", "error"]);

  const { addAlertBanner } = useContext(AlertContext);

  const loginMutation = useLoginMutation();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateInputs())
      return;

    const data = new FormData(event.currentTarget);
    const email = data.get("email");
    const password = data.get("password");

    loginMutation.mutate(
      { 
        data: {
          username: email, 
          password: password
        } 
      },
      {
        onError: (error) => {
          if (!error.response) {
            // Network error or no response
            addAlertBanner(t("error:networkError"), "error", 4000);
            return;
          }

          const statusCode = error.response.status;
          const wwwAuthHeader = error.response.headers?.['www-authenticate'];
          
          if (statusCode === 401) {
            // Invalid credentials
            addAlertBanner(t("error:invalidCredentials"), "error", 4000);
          } else if (statusCode === 403) {
            // Handle different 403 cases based on WWW-Authenticate header
            if (wwwAuthHeader?.includes("account_not_verified")) {
              addAlertBanner(t("error:accountNotVerified"), "warning", 6000);
            } else if (wwwAuthHeader?.includes("social_login_required")) {
              addAlertBanner(t("error:socialLoginRequired"), "info", 6000);
            } else {
              addAlertBanner(t("error:forbiddenAction"), "error", 4000);
            }
          } else if (statusCode >= 500) {
            // Server errors
            addAlertBanner(t("error:serverError"), "error", 4000);
          } else {
            // All other errors
            addAlertBanner(
              t("error:somethingGoesWrong"), 
              "error", 
              4000
            );
          }
        }
      }
    );
    
  };

  const validateInputs = () => {
    const email = document.getElementById("email");
    const password = document.getElementById("password");

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      isValid = false;
    }

    if (!password.value || password.value.length < 8) {
      isValid = false;
    }

    return isValid;
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        

        <Box
          sx={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            display: "flex",
            gap: "0.5rem",
          }}
        >
          <LanguageSelect />
        </Box>
        <Card variant="outlined">
          <MuscleHeadTextIcon
            sx={{
              alignSelf: "center",
              height: "160px",
              width: "100%",
              marginBottom: "40px",
            }}
          />
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            {t("signin")}
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color="primary"
                sx={{ ariaLabel: "email" }}
              />
            </FormControl>
            <FormControl>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <FormLabel htmlFor="password">{t("password")}</FormLabel>
                <Link
                  component={RouterLink} to="/reset-password"
                  type="button"
                  variant="body2"
                  sx={{ alignSelf: "baseline" }}
                >
                  {t("forgotPassword")}
                </Link>
              </Box>
              <TextField
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color="primary"
              />
            </FormControl>
            <Button

              type="submit"
              fullWidth
              variant="contained"
              // onClick={validateInputs}
              sx={{ marginTop: 4
               }}
            >
              {t("signin", { context: "button" })}
            </Button>
          </Box>

          <Link
            type="button"
            variant="body2"
            href={"mailto:" + getOurSupportEmail()}
            sx={{ alignSelf: "center", color: "gray" }} // TODO: link the color with the theme
          >
            {t("contactUs")}
          </Link>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
}
